
		import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "dreamland/jsx-runtime";
export const title = "How I hacked an obscure set-top box from 2013";
export const tags = ["Writeup", "Hardware"];
export const image = "/blog-images/jadoo3.webp";
export const description = "How I gained root access to a Jadoo3 set-top box and what I discovered during the investigation.";
function _createMdxContent(props) {
  const _components = {
    blockquote: "blockquote",
    code: "code",
    h2: "h2",
    img: "img",
    p: "p",
    pre: "pre",
    strong: "strong",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/blog-images/jadoo3.webp",
        alt: "Jadoo3"
      })
    }), "\n", _jsx(_components.p, {
      children: "Hello, friends! Today, I'd like to go over how I got root shell access to the Jadoo3, an obscure set-top box from 2013."
    }), "\n", _jsx(_components.h2, {
      children: "What is the Jadoo3?"
    }), "\n", _jsx(_components.p, {
      children: "As you can extrapolate from the name, the Jadoo3 is the third set-top box by JadooTV. The device aims to provide on-demand content from South Asia and the Middle East, with the name originating from the Hindi word for \"magic\"."
    }), "\n", _jsx(_components.p, {
      children: "I've had the device for a while, but haven't found anything to do with it until now. As such, it's been collecting dust in a half-disassembled state for some time."
    }), "\n", _jsx(_components.h2, {
      children: "Internals"
    }), "\n", _jsx(_components.p, {
      children: "Opening up the device, we can see the heart of the machine."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/blog-images/jadoo3-pcb-1.jpg",
        alt: "Jadoo3 PCB, top view"
      })
    }), "\n", _jsx(_components.p, {
      children: "In the center, we can see some sort of SoC, with a model number of VSI1871A-CBE3. Searching for this online returns very few results, with no datasheet available."
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "NOTE:"
        }), " Just off screen to the bottom left of the image, there seems to be a 10-pin header, which could be a debugging interface such as JTAG. However, this was not needed, as will be discussed later in the article."]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/blog-images/jadoo3-pcb-2.jpg",
        alt: "Jadoo3 PCB, front-left view"
      })
    }), "\n", _jsx(_components.p, {
      children: "However, in the front-left corner of the PCB, we can see some identifying information. The device seems to be based on the Syabas Popbox V8, another set-top box which is powered by the SMP8670, a MIPS CPU created by Sigma Designs."
    }), "\n", _jsx(_components.h2, {
      children: "Getting in"
    }), "\n", _jsxs(_components.p, {
      children: ["Booting the device up and getting it connected to a network, we can run ", _jsx(_components.code, {
        children: "nmap"
      }), " to reveal an open telnet service on port 23. This can't be good."]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/blog-images/love-in-the-air.jpg",
        alt: "Love is in the air? Wrong. Exposed root shell on port 23"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["That's right, a root shell was exposed on an open, unauthenticated channel. From there, it was as simple as connecting with ", _jsx(_components.code, {
        children: "nc <ip> 23"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["What's very interesting is that the box seems to run on a flavor of Linux. Running ", _jsx(_components.code, {
        children: "uname -a"
      }), " gives us the following output:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        children: "Linux JADOO 2.6.29.6-22-sigma #263 PREEMPT Thu Feb 12 17:19:10 MYT 2015 mips GNU/Linux\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["This seems to confirm this is a MIPS processor by Sigma Designs, as discussed earlier. The contents of ", _jsx(_components.code, {
        children: "/proc/cpuinfo"
      }), " gives us more insight:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        children: "system type             : Sigma Designs TangoX\nprocessor               : 0\ncpu model               : MIPS 24Kc V7.12  FPU V0.0\nBogoMIPS                : 466.94\nwait instruction        : yes\nmicrosecond timers      : yes\ntlb_entries             : 32\nextra interrupt vector  : yes\nhardware watchpoint     : yes, count: 4, address/irw mask: [0x0ffc, 0x0ffc, 0x0ffb, 0x0ffb]\nASEs implemented        : mips16\nshadow register sets    : 1\ncore                    : 0\nVCED exceptions         : not available\nVCEI exceptions         : not available\n\nSMP8XXX Chip ID         : 8671\nSMP8XXX Rev ID          : 2\nSystem bus frequency    : 351000000 Hz\nCPU frequency           : 702000000 Hz\nDSP frequency           : 351000000 Hz\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "Specifically, it seems to be a revised version of the SMP8670, called the SMP8671."
    }), "\n", _jsxs(_components.p, {
      children: ["Checking ", _jsx(_components.code, {
        children: "/proc/meminfo"
      }), " indicates the device comes with ~128MB of RAM."]
    }), "\n", _jsx(_components.h2, {
      children: "What now?"
    }), "\n", _jsx(_components.p, {
      children: "The device's interface is apparently Flash-based, so it might be possible to replace some of the included apps with arbitrary swf files. Additionally, the standard Linux framebuffer is exposed, which could open up the possibility of running non-flash programs (DOOM anyone?)."
    }), "\n", _jsx(_components.p, {
      children: "I will most likely make one or more follow-up posts if I manage to get my own programs running on the device."
    }), "\n", _jsx(_components.p, {
      children: "For now, this has been BomberFish, and I do declare; end broadcast."
    })]
  });
}
export function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}


		export default function Page() {
			const {wrapper: MDXLayout} = this.components || ({});
			return (
				MDXLayout 
					? _jsx(MDXLayout, { children: [_createMdxContent(this)], ...this })
					: _createMdxContent(this)
			)
		}
	